import type { AjudaDaPagina, ChaveDePagina } from "./tipos";

export const AJUDA_DA_CLINICA = {
  inicio: {
    titulo: "Início",
    resumo:
      "Resumo do dia da organização ativa. Na clínica, a agenda e os atalhos do dia a dia; no laboratório, os números da produção e os prazos.",
    passos: [
      "Para agendar ou pedir prótese rápido (clínica): use os atalhos Agendar consulta e Pedir prótese no topo da página.",
      "Para ver a agenda (clínica): troque entre Dia, Semana e Mês no cartão da agenda e clique numa consulta para ver os detalhes.",
      "Para acompanhar a produção (laboratório): confira Pedidos pendentes, Em produção, Atrasados e Em retrabalho, e o que vence hoje e nos próximos dias.",
      "Para terminar de montar o laboratório: siga a lista Complete seu laboratório (equipamentos, ficha técnica, preços, estoque e primeira clínica).",
      "No acesso Pessoal: se você também trabalha em outras clínicas, marque no filtro acima da agenda quais organizações entram nela.",
    ],
    dicas: [
      "No laboratório, quem não pode ver toda a produção vê só Meus trabalhos: os serviços delegados a você.",
      "Convites para entrar em outra organização aparecem no topo do Início até você aceitar ou recusar.",
    ],
  },
  agenda: {
    titulo: "Agenda",
    resumo: "As consultas da clínica por dia, semana, quinzena ou mês, com o status de cada atendimento.",
    passos: [
      "Para agendar: clique em Nova consulta (ou dê dois cliques num horário livre da grade), escolha o paciente, o procedimento e o horário e salve.",
      "Para remarcar: arraste a consulta para outro horário ou outro dia na grade.",
      "Para mudar o status: clique na consulta e escolha Agendado, Confirmado, Em espera, Atendido, Faltou ou Cancelado. Use Iniciar atendimento quando o paciente entrar e Encerrar atendimento ao terminar.",
      "Para ir a outra data: use as setas ou o botão Calendário, e alterne entre Dia, Semana, Quinzena e Mês.",
      "Para ver só parte da agenda: filtre por profissional e por procedimento na barra de cima.",
    ],
    dicas: [
      "Em Calendário › Fixar calendário, o mini calendário fica aberto ao lado da grade (só no computador).",
      "Quem não tem permissão para ver a agenda de todos vê apenas as próprias consultas.",
      "Horários da grade, visual dos cards e lembretes ficam em Configurações › Agenda.",
    ],
  },
  pacientes: {
    titulo: "Pacientes",
    resumo: "A lista de pacientes da organização, com busca, filtro por situação e cadastro.",
    passos: [
      "Para cadastrar: clique em Novo paciente. Nome e telefone bastam para começar; o resto você completa depois na ficha.",
      "Para o paciente se cadastrar sozinho: clique em Gerar link e envie o link a ele, por WhatsApp, por exemplo.",
      "Para achar alguém: digite em Buscar pacientes e use o filtro Ativos, Inativos ou Todos.",
      "Para abrir a ficha: clique no paciente na lista.",
      "Para levar a lista para uma planilha: use Exportar, escolha o formato e as colunas e clique em Baixar.",
    ],
    dicas: [
      "Alterne entre Lista e Cartões para ver os pacientes do jeito que preferir.",
      "No acesso Pessoal, se você trabalha em outras clínicas, a lista junta os pacientes delas, com filtro por organização.",
      "O arquivo exportado serve de modelo para importar pacientes em Configurações › Importar.",
      "Exportar é uma permissão à parte, ligada por padrão só para o proprietário e o administrador. Cada exportação fica no Registro de exportações.",
    ],
  },
  "ficha-do-paciente": {
    titulo: "Ficha do paciente",
    resumo: "Tudo de um paciente num lugar só: dados, anamnese, consultas, planos de tratamento, próteses, arquivos e histórico.",
    passos: [
      "Para agendar sem sair da ficha: clique em Agendar consulta no topo.",
      "Para editar os dados: na aba Dados, clique no lápis do campo, altere e aperte Enter (Esc cancela).",
      "Para montar um plano de tratamento: clique em Criar plano ou vá à aba Tratamentos. Cada plano é também o orçamento que o paciente leva.",
      "Para a anamnese: na aba Anamnese, preencha na clínica ou use Enviar ao paciente para ele responder por um link antes da consulta. Depois, o dentista revisa.",
      "Para pedir uma prótese deste paciente: clique em Pedir prótese.",
    ],
    dicas: [
      "Algumas abas (Anamnese, Odontograma, Tratamentos, Arquivos e Financeiro) dependem do plano da organização; Próteses depende da permissão de prótese.",
      "As respostas da anamnese são dado de saúde: só abrem para quem tem a permissão Ver anamneses.",
      "O ícone de WhatsApp ao lado do nome abre uma conversa com o paciente.",
    ],
  },
  "ficha-em-outra-clinica": {
    titulo: "Ficha em outra clínica",
    resumo:
      "A ficha de um paciente de outra clínica em que você trabalha, aberta pelo acesso Pessoal sem trocar de organização. Aqui ela é só leitura.",
    passos: [
      "Para consultar: navegue pelas abas normalmente. Você vê o que o seu papel naquela clínica permite.",
      "Para agendar ou pedir prótese nessa clínica: use Agendar consulta ou Pedir prótese, que funcionam daqui mesmo.",
      "Para editar qualquer dado: clique em Abrir na clínica para editar. A organização ativa passa a ser essa clínica.",
    ],
    dicas: ["A faixa no topo mostra de qual clínica é a ficha."],
  },
  protese: {
    titulo: "Prótese",
    resumo: "Os pedidos de prótese enviados aos laboratórios e os laboratórios parceiros da clínica.",
    passos: [
      "Para pedir uma prótese: clique em Novo pedido, escolha o paciente e o laboratório, monte os serviços e confirme o envio. O pedido cai direto na fila do laboratório.",
      "Para acompanhar: na aba Pedidos, filtre pelo status e alterne entre cards e lista. Clique num pedido para ver os detalhes.",
      "Para conectar um laboratório: na aba Parceiros, clique em Novo parceiro e busque o laboratório pelo nome ou convide por link. A conexão vale quando o laboratório aceitar.",
      "Para pedir uma correção: abra o pedido e use Solicitar retrabalho, descrevendo o que precisa ser ajustado.",
      "Para ver os pedidos separados por profissional: use Por dentista, ao lado de Todos os pedidos.",
    ],
    dicas: [
      "Quem não pode ver todos os pedidos vê só os que criou, e não tem a opção Por dentista.",
      "No acesso Pessoal de quem está em outras organizações, Pedidos mostra os de todas: use o filtro de organizações. O de outra clínica abre só leitura; para agir nele, use Abrir em.",
    ],
  },
  procedimentos: {
    titulo: "Procedimentos",
    resumo: "Os procedimentos que a clínica faz, agrupados por especialidade, com preço, duração e cor.",
    passos: [
      "Para adicionar: clique em Adicionar procedimento e escolha da base da plataforma (os Sugeridos vêm das especialidades da clínica). Não achou? Crie o seu ali mesmo.",
      "Para definir o preço: clique no preço na própria linha (ou em Definir preço, quando falta) e confirme com Enter.",
      "Para achar o que está sem valor: clique no botão âmbar que conta os procedimentos sem preço.",
      "Para mudar duração, cor e outros dados: clique no procedimento.",
      "Para mexer em vários de uma vez: clique em Selecionar, marque os procedimentos e escolha Ativar, Desativar ou Excluir.",
    ],
    dicas: [
      "Mudar preço exige a permissão de editar preços e pede o PIN de segurança.",
      "Desativar tira o procedimento da lista ativa sem apagar o histórico; dá para reativar depois.",
      "É um recurso do plano pago. Sem ele, o procedimento é descrito em texto livre na consulta.",
    ],
  },
  arquivos: {
    titulo: "Arquivos",
    resumo:
      "Os arquivos da organização, organizados por pacientes, parceiros, pedidos e serviços. No laboratório, inclui os arquivos que as clínicas enviam nos pedidos.",
    passos: [
      "Para enviar arquivos: entre na pasta e clique em Anexar arquivos, ou arraste os arquivos para a página.",
      "Para organizar: crie pastas em Nova pasta. Os atalhos Pacientes, Parceiros, Pedidos e Serviços já vêm prontos.",
      "Para achar um arquivo: use a busca de arquivos, que procura por nome, paciente, dentista, clínica, laboratório, serviço ou tag em todas as pastas.",
      "Para compartilhar: no menu de ações do arquivo, clique em Compartilhar e copie o link público. Você pode revogar quando quiser.",
      "No mesmo menu: Baixar, Renomear, Mover, Tags, Histórico e Excluir.",
    ],
    dicas: [
      "Alterne entre grade (melhor para imagens e modelos 3D) e lista (nome, tamanho e data).",
      "Só quem tem a permissão de gerenciar arquivos vê esta área.",
    ],
  },
  "minha-clinica": {
    titulo: "Minha clínica",
    resumo: "A gestão da clínica num lugar só: as salas e a equipe. O estoque da clínica chega em breve.",
    passos: [
      "Para ver o que acontece em cada cadeira: clique no cartão Salas. Dali saem a planta ao vivo, a lista, a ocupação do mês, o modo recepção e o cadastro de salas.",
      "Para convidar alguém ou mudar o acesso de uma pessoa: clique no cartão Equipe.",
    ],
    dicas: [
      "Salas é um recurso do plano pago.",
      "A planta muda de tamanho no editor, em Tamanho da planta. Clínica de dois andares: desenhe os andares lado a lado e identifique cada um com um Texto.",
      "Quem não pode gerenciar a equipe vê só o cartão Salas.",
    ],
  },
} satisfies Partial<Record<ChaveDePagina, AjudaDaPagina>>;
